<template>
  <div
    v-if="open || delayed"
    ref="self"
    class="tooltip-container position-fixed d-block p-3"
    :class="[orient, { delayed: delayed, 'delayed-visible': delayed && open }]"
    :style="`left: ${pos.x}px; top: ${pos.y}px; visibility: ${visible}`"
  >
    <slot />
  </div>
</template>

<script lang="ts">
import { IPoint } from '@/store/ui/interfaces';
import { PropType } from 'vue';

export default {
  props: {
    open: { type: Boolean, default: false },
    orient: { type: String, default: 'bottom' },
    position: {
      type: Object as PropType<IPoint>,
      default: () => {
        return { x: 0, y: 0 }
      },
    },
    delayed: { type: Boolean, default: false },
  },

  data: () => {
    return {
      offset: {
        top: { x: 0, y: 0 - 0.5 * (Math.sqrt(2) * 12) },
        bottom: { x: 0, y: 0.5 * (Math.sqrt(2) * 12) },
        right: { x: 0, y: 0.5 * (Math.sqrt(2) * 12) },
        left: { x: 0, y: 0.5 * (Math.sqrt(2) * 12) },
      },
      pos: { x: 0, y: 0 },
      visible: 'hidden',
    }
  },

  watch: {
    open(newValue) {
      setTimeout(() => {
        if (!!newValue) {
          this.updatePos()
          this.visible = 'visible'
        } else {
          this.visible = 'hidden'
        }
      }, 16)
    },
  },

  methods: {
    vectorAdd: (initialPosition: IPoint, bonusOffset: IPoint) => {
      return { x: initialPosition.x + bonusOffset.x, y: initialPosition.y + bonusOffset.y }
    },
    updatePos() {
      const self = this.$refs.self as HTMLElement
      
      if (!self) return // in case the user hovers too fast past the element and the tooltip is not rendered yet

      const verticalPosition = this.orient === 'top' ? -self.getClientRects()[0].height : 15
      const position = this.vectorAdd(this.position, { x: -16, y: verticalPosition })

      this.pos = this.vectorAdd(position, this.offset[this.orient as keyof typeof this.offset])
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.delayed {
  opacity: 0;
}

.delayed-visible {
  opacity: 1;
  transition: opacity 0s linear 1s;
}

.tooltip-container {
  z-index: 114;
  transform: translateZ(0);
  min-height: 28px;
  min-width: 50px;
  background-color: $color-aida-white;
  box-shadow: 0 3px 9px 0 rgba(0, 0, 0, 0.3);
  font-size: $default-font-size;
  white-space: nowrap;
  font-family: 'Open Sans', sans-serif;
  border: 1px solid $color-expinet-gray;

  &:after {
    background-color: $color-aida-white;
    content: '';
    display: block;
    position: absolute;
    width: 12px;
    height: 12px;
    transform: rotate(45deg);
  }

  &.right:after {
    right: 12px;
  }

  &.left:after {
    left: 12px;
  }

  &.bottom:after,
  &.left:after,
  &.right:after {
    top: -6px;
    border-top: 1px solid $color-expinet-gray;
    border-left: 1px solid $color-expinet-gray;
  }

  &.top:after {
    bottom: -6px;
    border-bottom: 1px solid $color-expinet-gray;
    border-right: 1px solid $color-expinet-gray;
  }
}
</style>
